import React from 'react';
import Card from './Card';
import CardHeader from './CardHeader';
import CardContent from './CardContent';
import CardActions from './CardActions';

export interface ConfigField {
  id: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'number' | 'date' | 'select' | 'textarea' | 'section';
  value: string | number;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  options?: Array<{ value: string; label: string }>;
  validation?: {
    pattern?: string;
    min?: number;
    max?: number;
    message?: string;
  };
}

export interface ConfigCardProps {
  title?: string;
  subtitle?: string;
  fields: ConfigField[];
  onFieldChange: (fieldId: string, value: string | number) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  isSubmitting?: boolean;
  errors?: Record<string, string>;
  className?: string;
}

const ConfigCard: React.FC<ConfigCardProps> = ({
  title,
  subtitle,
  fields,
  onFieldChange,
  onSubmit,
  onCancel,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  isSubmitting = false,
  errors = {},
  className = '',
}) => {
  const renderField = (field: ConfigField) => {
    // Handle section headers separately
    if (field.type === 'section') {
      return null; // Section headers are rendered in the parent div
    }

    const baseClasses = `w-full px-3 py-2 border rounded-lg onyx-body text-sm text-gray-900 transition-colors ${
      errors[field.id]
        ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 bg-white focus:border-onyx-bright-blue focus:ring-onyx-bright-blue'
    } ${field.disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`;

    switch (field.type) {
      case 'select':
        return (
          <select
            value={field.value}
            onChange={(e) => onFieldChange(field.id, e.target.value)}
            disabled={field.disabled || isSubmitting}
            className={baseClasses}
            required={field.required}
          >
            <option value="">{field.placeholder || `Select ${field.label}`}</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            value={field.value}
            onChange={(e) => onFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            disabled={field.disabled || isSubmitting}
            className={`${baseClasses} resize-none`}
            rows={3}
            required={field.required}
          />
        );

      default:
        return (
          <input
            type={field.type}
            value={field.value}
            onChange={(e) =>
              onFieldChange(
                field.id,
                field.type === 'number' ? Number(e.target.value) : e.target.value
              )
            }
            placeholder={field.placeholder}
            disabled={field.disabled || isSubmitting}
            className={baseClasses}
            required={field.required}
            min={field.validation?.min}
            max={field.validation?.max}
            pattern={field.validation?.pattern}
          />
        );
    }
  };

  return (
    <Card variant="config" size="md" hover={false} className={className}>
      {/* Header */}
      {(title || subtitle) && <CardHeader title={title} subtitle={subtitle} border={true} />}

      {/* Form Content */}
      <CardContent>
        <div className="space-y-4">
          {fields.map((field, index) => {
            // Render section headers
            if (field.type === 'section') {
              return (
                <div key={field.id} className={index > 0 ? 'pt-4' : ''}>
                  <h3 className="text-sm font-semibold text-gray-900 onyx-subtitle pb-2 border-b border-gray-200">
                    {field.label}
                  </h3>
                </div>
              );
            }

            // Render regular fields
            return (
              <div key={field.id}>
                <label className="block mb-2 text-sm onyx-subtitle text-gray-700">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>

                {renderField(field)}

                {errors[field.id] && (
                  <p className="mt-1 text-xs text-red-600 onyx-body">{errors[field.id]}</p>
                )}

                {field.validation?.message && !errors[field.id] && (
                  <p className="mt-1 text-xs text-gray-500 onyx-body">{field.validation.message}</p>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>

      {/* Actions */}
      <CardActions border={true}>
        <div className="flex space-x-3 w-full">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 onyx-subtitle rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2 bg-onyx-bright-blue text-white onyx-subtitle rounded-lg hover:bg-onyx-dark-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Saving...' : submitLabel}
          </button>
        </div>
      </CardActions>
    </Card>
  );
};

export default ConfigCard;
